import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import FontSizeSelector, { FontSizeOption } from '@/components/ui/FontSizeSelector';

import SettingsForm from "./supabase-data/SettingsForm";
import PreviewSection from "./supabase-data/PreviewSection";
import { useFetchPreview } from "./supabase-data/useFetchPreview";

interface SupabaseDataEditorProps {
  content: {
    url?: string;
    refreshInterval?: number;
    showDescriptions?: boolean;
    fontSize?: FontSizeOption;
    selectedSections?: string[];
    displayOptions?: {
      showTitle: boolean;
      showDescription: boolean;
      showItems: boolean;
      showColumns: boolean;
    };
  };
  onSave?: (content: any) => void;
  onContentChange?: (field: string, value: any) => void;
}

const SupabaseDataEditor: React.FC<SupabaseDataEditorProps> = ({ content, onSave, onContentChange }) => {
  const [activeTab, setActiveTab] = useState<string>("settings");
  
  // Provide default values if content is incomplete
  const defaultValues = {
    url: content?.url || "https://wwosfhatkzqsgzhkigpx.supabase.co/functions/v1/daily-study",
    refreshInterval: content?.refreshInterval || 5,
    showDescriptions: content?.showDescriptions !== undefined ? content.showDescriptions : true,
    fontSize: content?.fontSize || 'md',
    selectedSections: content?.selectedSections || [],
    displayOptions: content?.displayOptions || {
      showTitle: true,
      showDescription: true,
      showItems: true,
      showColumns: true
    }
  };
  
  const form = useForm({
    defaultValues
  });
  
  const currentUrl = form.watch("url");
  
  const {
    previewData,
    isLoading,
    previewError,
    isCorsError,
    fetchPreviewData,
    showPreview,
    togglePreview
  } = useFetchPreview(currentUrl);

  const handleFontSizeChange = (value: FontSizeOption) => {
    form.setValue('fontSize', value);
    if (onContentChange) {
      onContentChange('fontSize', value);
    }
  };

  const handleSubmit = (values: any) => {
    console.log("Saving Supabase data settings:", values);
    
    // Ensure all values are properly formatted
    const formattedValues = {
      url: values.url,
      refreshInterval: Number(values.refreshInterval),
      showDescriptions: values.showDescriptions,
      fontSize: values.fontSize,
      selectedSections: Array.isArray(values.selectedSections) ? values.selectedSections : [],
      displayOptions: {
        showTitle: values.displayOptions.showTitle,
        showDescription: values.displayOptions.showDescription,
        showItems: values.displayOptions.showItems,
        showColumns: values.displayOptions.showColumns
      }
    };
    
    console.log("Formatted values for saving:", formattedValues);
    
    if (onSave) {
      onSave(formattedValues);
    } else if (onContentChange) {
      // If using onContentChange, update each field individually
      Object.entries(formattedValues).forEach(([key, value]) => {
        console.log(`Updating ${key}:`, value);
        onContentChange(key, value);
      });
    }
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid grid-cols-2 mb-6">
            <TabsTrigger value="settings">הגדרות</TabsTrigger>
            <TabsTrigger value="preview">תצוגה מקדימה</TabsTrigger>
          </TabsList>
          <TabsContent value="settings">
            <div className="space-y-4">
              <FontSizeSelector 
                value={form.watch('fontSize')} 
                onChange={handleFontSizeChange} 
                label="גודל גופן לכרטיסים" 
              />
              <SettingsForm 
                form={form} 
                onSubmit={handleSubmit} 
                isCorsError={isCorsError}
              />
            </div>
          </TabsContent>
          <TabsContent value="preview">
            <PreviewSection
              previewData={previewData}
              isLoading={isLoading}
              previewError={previewError}
              isCorsError={isCorsError}
              fetchPreviewData={fetchPreviewData}
              showPreview={showPreview}
              togglePreview={togglePreview}
              showDescriptions={form.watch('showDescriptions')}
              fontSize={form.watch('fontSize')}
              selectedSections={form.watch('selectedSections')}
              displayOptions={form.watch('displayOptions')}
            />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default SupabaseDataEditor;
